import { Component, OnInit } from "@angular/core";
import { CartService, CartItem } from "../../services/cart.service";
import { resolveImageUrl } from "src/app/core/utils/image.util";
import Swal from "sweetalert2";
import { Router } from "@angular/router";

export interface AppliedDiscount {
  code: string;
  discountAmount: number;
  message: string;
  details?: any;
}

@Component({
  selector: "app-cart",
  templateUrl: "./cart.component.html",
  styleUrls: ["./cart.component.css"],
})
export class CartComponent implements OnInit {
  cartItems: CartItem[] = [];
  couponCode: string = '';
  appliedDiscounts: AppliedDiscount[] = [];
  loadingCoupon = false;

  constructor(
    private cartService: CartService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.cartService.cart$.subscribe((items) => {
      this.cartItems = items;
    });
  }

  resolveImg(imgUrl: any): string {
    if (!imgUrl) return 'assets/img/no-image.png';
    let url = '';
    if (Array.isArray(imgUrl) && imgUrl.length > 0) {
      url = imgUrl[0];
    } else if (typeof imgUrl === 'string') {
      const trimmed = imgUrl.trim();
      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed.replace(/'/g, '"'));
          url = Array.isArray(parsed) ? parsed[0] : trimmed;
        } catch {
          url = trimmed;
        }
      } else if (trimmed.includes(',')) {
        url = trimmed.split(',')[0].trim();
      } else {
        url = trimmed;
      }
    }
    return resolveImageUrl(url);
  }

  onImageError(event: any): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"><rect width="200" height="200" fill="%23f1f5f9"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="14">Sin Imagen</text></svg>';
    }
  }

  increaseQuantity(item: CartItem): void {
    this.cartService.updateQuantity(item.product.id, item.size, item.color, item.quantity + 1);
  }

  deincreaseQuantity(item: CartItem): void {
    if (item.quantity > 1) {
      this.cartService.updateQuantity(item.product.id, item.size, item.color, item.quantity - 1);
    }
  }

  removeItem(item: CartItem): void {
    Swal.fire({
      title: '¿Remover producto?',
      text: `¿Deseas eliminar "${item.product.name}" de tu carrito?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.cartService.removeFromCart(item.product.id, item.size, item.color);
      }
    });
  }

  clearAllCart(): void {
    Swal.fire({
      title: '¿Vaciar carrito?',
      text: 'Se eliminarán todos los productos seleccionados.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, vaciar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.cartService.clearCart();
        this.appliedDiscounts = [];
      }
    });
  }

  getSubtotal(): number {
    return this.cartItems.reduce((total, item) => {
      return total + (item.product.price * item.quantity);
    }, 0);
  }

  getTotalDiscount(): number {
    return this.appliedDiscounts.reduce((sum, d) => sum + d.discountAmount, 0);
  }

  getTotal(): number {
    const subtotal = this.getSubtotal();
    const discount = this.getTotalDiscount();
    return Math.max(0, subtotal - discount);
  }

  applyCoupon(): void {
    if (!this.couponCode.trim()) {
      Swal.fire({ icon: 'warning', title: 'Cupón Vacío', text: 'Por favor ingresa un código de cupón.' });
      return;
    }

    const code = this.couponCode.toUpperCase().trim();
    if (this.appliedDiscounts.some(c => c.code === code)) {
      Swal.fire({ icon: 'info', title: 'Cupón ya aplicado', text: 'Este cupón ya está activo en tu compra.' });
      return;
    }

    this.loadingCoupon = true;
    const subtotal = this.getSubtotal();

    this.cartService.validateCoupon(code, subtotal).subscribe(
      (res) => {
        this.loadingCoupon = false;
        if (res && res.valid) {
          this.appliedDiscounts.push({
            code: code,
            discountAmount: res.discountAmount || 0,
            message: res.message,
            details: res.discount
          });
          this.couponCode = '';
          Swal.fire({
            icon: 'success',
            title: '¡Cupón Válido!',
            text: res.message || `Descuento de $${res.discountAmount.toLocaleString()} COP aplicado.`
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Cupón Inválido',
            text: res?.message || 'El cupón no es válido o ha alcanzado su límite.'
          });
        }
      },
      (err) => {
        this.loadingCoupon = false;
        console.error('Error al validar cupón:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error de Validación',
          text: err?.error?.error?.message || err?.error?.message || 'No se pudo conectar con el servidor de cupones.'
        });
      }
    );
  }

  removeCoupon(code: string): void {
    this.appliedDiscounts = this.appliedDiscounts.filter(c => c.code !== code);
  }

  proceedToCheckout(): void {
    if (this.cartItems.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Carrito Vacío', text: 'Agrega al menos un producto para proceder al pago.' });
      return;
    }

    const cartInfo = {
      items: this.cartItems,
      subtotal: this.getSubtotal(),
      total: this.getTotal(),
      appliedCoupons: this.appliedDiscounts.map(d => ({ code: d.code, discountAmount: d.discountAmount })),
      totalDiscount: this.getTotalDiscount()
    };
    localStorage.setItem("cartInfo", JSON.stringify(cartInfo));

    this.router.navigate(['/checkout']);
  }
}
